/**
 * @file AppContext.jsx
 * Contesto globale dell'applicazione.
 *
 * Gestisce tramite useReducer:
 * - Autenticazione (userRole, userName, userEmail, isAuthenticated, isAuthReady)
 * - Navigazione globale (mese, anno, PM selezionato)
 * - UI globale (sidebar collapsed, toasts)
 * - Lista project managers (caricata una volta al login)
 *
 * Lo stato di autenticazione viene inizialmente letto da localStorage (sentio_role).
 */

import { createContext, useReducer, useCallback, useEffect } from 'react';
import { ROLES } from '../lib/constants/roles';
import { getManagerList } from '../lib/api/planningModal';
import { logout as apiLogout, loginWithAzureToken } from '../lib/api/auth';
import { msalInstance, msalReady } from '../lib/msalConfig';

export const AppContext = createContext(null);

const now = new Date();
// Il mese preselezionato è sempre il mese successivo a quello corrente
const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

const initialState = {
  userRole:           ROLES.PREDEFINITO,
  userRoles:          [],
  userName:           '',
  userEmail:          '',
  isAuthReady:        false,
  isAuthenticated:    false,
  toasts:             [],
  isSidebarCollapsed: false,
  month:              nextMonthDate.getMonth() + 1,
  year:               nextMonthDate.getFullYear(),
  pmId:               null,
  projectManagers:    [],
  selectedPmName:     '',
  userPmList:         [],
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_AUTH':
      localStorage.setItem('sentio_role', action.role);
      // NOTA: dopo ogni SET_AUTH (login o cambio ruolo) il consumer deve
      // chiamare anche refreshPermissions() da usePermissions() per
      // ri-sincronizzare i permessi dal backend.
      return {
        ...state,
        userRole:        action.role,
        userRoles:       action.roles ?? state.userRoles,
        userEmail:       action.email ?? state.userEmail,
        userName:        action.name ?? state.userName,
        isAuthReady:     true,
        isAuthenticated: true,
      };
    case 'SET_AUTH_READY':
      return { ...state, isAuthReady: true, isAuthenticated: false };
    case 'LOGOUT':
      return { ...initialState, isAuthReady: true, isAuthenticated: false };
    case 'TOGGLE_SIDEBAR':
      return { ...state, isSidebarCollapsed: !state.isSidebarCollapsed };
    case 'COLLAPSE_SIDEBAR':
      return { ...state, isSidebarCollapsed: true };
    case 'EXPAND_SIDEBAR':
      return { ...state, isSidebarCollapsed: false };
    case "ADD_TOAST":
      return { ...state, toasts: [...state.toasts, action.toast] };
    case "REMOVE_TOAST":
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.id),
      };
    case "SET_MONTH":
      return { ...state, month: action.month };
    case "SET_YEAR":
      return { ...state, year: action.year };
    case "SET_PM_ID":
      return { ...state, pmId: action.pmId };
    case "SET_PROJECT_MANAGERS":
      return { ...state, projectManagers: action.projectManagers };
    case "SET_SENIORS":
      return { ...state, seniors: action.seniors };
    case "SET_PM_NAME":
      return { ...state, selectedPmName: action.name };
    case "SET_USER_PM_LIST":
      return { ...state, userPmList: action.userPmList };
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Feature flag per abilitare/disabilitare il flusso Azure AD SSO.
  // Se VITE_AZURE_ENABLED è 'false' (stringa), salta completamente l'auth check.
  // Default: true (se la var non è impostata, !== 'false' restituisce true).
  const azureEnabled = import.meta.env.VITE_AZURE_ENABLED !== 'false';

  const setAuth           = useCallback((role, roles, email, name) => dispatch({ type: 'SET_AUTH', role, roles, email, name }), []);
  const logout            = useCallback(async () => {
    await apiLogout();
    if (azureEnabled) {
      // Pulisce la cache MSAL per evitare re-autenticazione silenziosa al prossimo login
      await msalReady;
      msalInstance.clearCache();
    }
    dispatch({ type: 'LOGOUT' });
    window.history.replaceState(null, '', '/');
  }, [azureEnabled]);
  const toggleSidebar     = useCallback(() =>         dispatch({ type: 'TOGGLE_SIDEBAR' }),                []);
  const collapseSidebar   = useCallback(() =>         dispatch({ type: 'COLLAPSE_SIDEBAR' }),              []);
  const expandSidebar     = useCallback(() =>         dispatch({ type: 'EXPAND_SIDEBAR' }),                []);
  const addToast          = useCallback((toast) =>    dispatch({ type: 'ADD_TOAST', toast }),              []);
  const removeToast       = useCallback((id) =>       dispatch({ type: 'REMOVE_TOAST', id }),              []);
  const setMonth          = useCallback((month) =>    dispatch({ type: 'SET_MONTH', month }),              []);
  const setYear           = useCallback((year) =>     dispatch({ type: 'SET_YEAR', year }),                []);
  const setPmId           = useCallback((pmId) =>     dispatch({ type: 'SET_PM_ID', pmId }),               []);
  const setSelectedPmName = useCallback((name) =>     dispatch({ type: 'SET_PM_NAME', name }),             []);
  const setUserPmList     = useCallback((userPmList) => dispatch({ type: 'SET_USER_PM_LIST', userPmList }), []);
  // Verifica sessione sul backend tramite JWT cookie
  useEffect(() => {
    // Se Azure è disabilitato, entra in modalità dev con accesso Admin completo
    if (!azureEnabled) {
      dispatch({ type: 'SET_AUTH', role: ROLES.ADMIN, email: 'dev@local', name: 'Dev Local' });
      return;
    }

    const initAuth = async () => {
      try {
        // Aspetta che MSAL sia inizializzato (richiesto in v3+)
        await msalReady;

        // Se l'app si sta avviando dopo un redirect Microsoft, MSAL processa qui
        // il codice di autorizzazione e restituisce un AuthenticationResult con idToken.
        const redirectResult = await msalInstance.handleRedirectPromise();

        if (redirectResult?.accessToken) {
          // L'access token (scope api://{clientId}/.default) contiene il claim "roles" con gli App Role.
          // Il BE lo valida, legge i ruoli dal token e genera il JWT applicativo (cookie httpOnly).
          await loginWithAzureToken(redirectResult.accessToken);
        }

        // Legge il JWT cookie (già presente o appena settato dal BE) per ottenere le info utente
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          const info = json.info;
          const name = [info?.nome, info?.cognome].filter(Boolean).join(' ');
          setAuth(info.role, info.roles, info.email ?? '', name);
          setUserPmList(info.pmList ?? []);
        } else {
          dispatch({ type: 'SET_AUTH_READY' });
        }
      } catch (err) {
        console.error('[Auth] Errore inizializzazione auth:', err);
        dispatch({ type: 'SET_AUTH_READY' });
      }
    };

    initAuth();
  }, [setAuth, azureEnabled]);

  // Carica la lista dei Project Manager
  useEffect(() => {
    if (!state.isAuthReady) return;

    const loadProjectManagers = async () => {
      try {
        const response = await getManagerList();
        dispatch({ type: 'SET_PROJECT_MANAGERS', projectManagers: response.pm });
      } catch (error) {
        console.error("Errore caricamento Project Manager:", error);
      }
    };

    loadProjectManagers();
  }, [state.isAuthReady]);

  // Carica la lista dei Senior
  useEffect(() => {
    if (!state.isAuthReady) return;

    const loadSeniors = async () => {
      try {
        const response = await getManagerList();
        dispatch({ type: 'SET_SENIORS', seniors: response.senior });
      } catch (error) {
        console.error("Errore caricamento Senior:", error);
      }
    };

    loadSeniors();
  }, [state.isAuthReady]);

  useEffect(() => {
    const hasManagers = state.projectManagers.length > 0;
    const hasUserName = Boolean(state.userName);

    if (!hasManagers || !hasUserName) return;

    const canEditPm = [ROLES.PLANNER, ROLES.ADMIN].includes(state.userRole);

    setSelectedPmName(
      !canEditPm && state.projectManagers.includes(state.userName)
        ? state.userName
        : ''
    );
  }, [state.projectManagers, state.userName, state.userRole, setSelectedPmName]);

  return (
    <AppContext.Provider value={{
      ...state,
      azureEnabled,       // Espone il flag Azure al resto dell'app (usato da LoginPage e TopBar)
      setAuth,
      logout,
      toggleSidebar,
      collapseSidebar,
      expandSidebar,
      addToast,
      removeToast,
      setMonth,
      setYear,
      setPmId,
      setSelectedPmName,
    }}>
      {children}
    </AppContext.Provider>
  );
}
